import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getBlocks } from '../../../services/GlobalRoles/getBlocks';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../../context/AppContext';
import { Dropdown } from "primereact/dropdown";
import { getLands } from '../../../services/GlobalRoles/getLands';
import { formatCurrency } from '../../../utils/formatCurrency';
import { getAllLands } from '../../../services/Register/getAllLands';
import { getGoogleMapImage } from '../../../services/GlobalRoles/getGoogleMapImage';
import { getGoogleMap } from '../../../services/Settings/GoogleMaps/getGoogleMap';
import { postMapImage } from '../../../services/GlobalRoles/postMapImage';
import { postGoogleMap } from '../../../services/Settings/GoogleMaps/postGoogleMap';
import { putLands } from '../../../services/Register/putLands';
import { toast } from 'react-toastify';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';

const API_KEY = "AIzaSyCLSP50BG6pM3YuGCAvCvQq39fXJ1xtggQ";

export const GoogleMapsCore = ({ type }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { nomenclatures, permissions } = useAppContext();
  const nomenclature = JSON.parse(nomenclatures);
  const queryClient = useQueryClient();
  const mapRef = useRef(null);
  const currentPolygonRef = useRef(null);
  const OverlayClass = useRef(null);
  const [map, setMap] = useState(null);
  const [drawingManager, setDrawingManager] = useState(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [lotes, setLotes] = useState([]);
  const [mapConfig, setMapConfig] = useState(null);
  const [polygons, setPolygons] = useState(new Map());
  const [imageOverlay, setImageOverlay] = useState(null);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [isEditingOverlay, setIsEditingOverlay] = useState(false);
  const [selectedLote, setSelectedLote] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [localImageConfig, setLocalImageConfig] = useState(null);

  // --- NOVO: mutation para salvar mapa e atualizar cache da imagem imediatamente ---
  const saveInitialMapMutation = useMutation({
    mutationFn: async ({ payload, imageUrl }) => {
      await postGoogleMap(payload);
      return imageUrl;
    },
    onSuccess: (imageUrl, variables) => {
      // Atualiza a query da imagem imediatamente para refletir no overlay
      queryClient.setQueryData([`map-image-${id}`], (old) => ({
        ...(old || {}),
        file: imageUrl,
      }));
      // Atualiza o config local para renderizar overlay imediatamente
      setLocalImageConfig({
        center: variables.payload.center,
        bounds: variables.payload.bounds,
        image: imageUrl,
        initial: true,
        rotation: 0,
        zoom: variables.payload.zoom || 16,
      });
      setShowSetupModal(false);
    },
    onError: (error) => {
      toast.error("Erro ao salvar configuração inicial do mapa.");
    }
  });

  const { data: imgGMaps, isLoading: isImgGMapsLoading } = useQuery({
    queryKey: [`map-image-${id}`],
    queryFn: () => getGoogleMapImage(id),
    retry: false,
    enabled: !!permissions?.includes("21")
  });

  const { data: dataGMaps, isLoading: isDataGMapsLoading } = useQuery({
    queryKey: [`google-map-${id}`],
    queryFn: () => getGoogleMap(id),
    retry: false,
    enabled: !!permissions?.includes("170")
  });

  const { data: allLandsData, isLoading: isAllLandsLoading } = useQuery({
    queryKey: [`all-lands-${id}`],
    queryFn: () => getAllLands(id),
    retry: false,
    enabled: !!permissions,
  });

  const saveMapMutation = useMutation(postGoogleMap, {
    onMutate: () => {
      return toast.loading("Salvando configurações do mapa...");
    },
    onSuccess: (_data, _variables, context) => {
      toast.update(context, {
        render: "Configurações do mapa salvas com sucesso!",
        autoClose: 5000,
        type: "success",
        isLoading: false,
      });
      queryClient.invalidateQueries([`google-map-${id}`]);
    },
    onError: (_error, _variables, context) => {
      toast.update(context, {
        render: "Falha ao salvar as configurações do mapa.",
        autoClose: 5000,
        type: "error",
        isLoading: false,
      });
    },
  });

  const updateLandMutation = useMutation(putLands, {
    onMutate: () => {
      return toast.loading("Salvando lote, por favor aguarde...");
    },
    onSuccess: (_data, _variables, context) => {
      toast.update(context, {
        render: "Lote salvo com sucesso!",
        autoClose: 5000,
        type: "success",
        isLoading: false,
      });
      queryClient.invalidateQueries([`all-lands-${id}`]);
      queryClient.invalidateQueries({
        predicate: query => query.queryKey[0].startsWith('lands_')
      });
    },
    onError: (error, _variables, context) => {
      toast.update(context, {
        render: "Falha ao salvar o lote.",
        autoClose: 5000,
        type: "error",
        isLoading: false,
      });
      console.error("Erro ao salvar lote:", error);
    }
  });

  useEffect(() => {
    if (window.google && window.google.maps) { setScriptLoaded(true); return; }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=drawing,geometry`;
    script.async = true; script.defer = true;
    script.onload = () => setScriptLoaded(true);
    document.head.appendChild(script);
    return () => { script.parentNode?.removeChild(script); };
  }, []);

  useEffect(() => {
    if (scriptLoaded && !OverlayClass.current) {
      class ManipulableImageOverlay extends window.google.maps.OverlayView {
        constructor(bounds, imageSrc, map) { super(); this.bounds_ = bounds; this.imageSrc_ = imageSrc; this.map_ = map; this.div_ = null; this.isDraggable_ = false; this.rotation_ = 0; this.setMap(map); }
        onAdd() { this.div_ = document.createElement('div'); this.div_.style.position = 'absolute'; this.div_.style.boxSizing = 'border-box'; this.div_.style.transformOrigin = 'center center'; const img = document.createElement('img'); img.src = this.imageSrc_; img.style.width = '100%'; img.style.height = '100%'; img.style.position = 'absolute'; img.style.userSelect = 'none'; img.style.pointerEvents = 'none'; this.div_.appendChild(img); this.getPanes().overlayLayer.appendChild(this.div_); this.addDragHandlers(); this.draw(); }
        draw() { const projection = this.getProjection(); if (!projection || !this.bounds_) { return; } const sw = projection.fromLatLngToDivPixel(this.bounds_.getSouthWest()); const ne = projection.fromLatLngToDivPixel(this.bounds_.getNorthEast()); if (!this.div_) return; this.div_.style.left = sw.x + 'px'; this.div_.style.top = ne.y + 'px'; this.div_.style.width = (ne.x - sw.x) + 'px'; this.div_.style.height = (sw.y - ne.y) + 'px'; this.div_.style.transform = `rotate(${this.rotation_}deg)`; }
        onRemove() { if (this.div_) { this.div_.parentNode.removeChild(this.div_); this.div_ = null; } }
        addDragHandlers() { this.div_.addEventListener('mousedown', (e) => { if (!this.isDraggable_) return; e.preventDefault(); e.stopPropagation(); this.map_.setOptions({ draggable: false }); let startPos = { x: e.clientX, y: e.clientY }; const onMouseMove = (moveEvent) => { const projection = this.getProjection(); if (!projection) return; const dx = moveEvent.clientX - startPos.x; const dy = moveEvent.clientY - startPos.y; startPos = { x: moveEvent.clientX, y: moveEvent.clientY }; const sw = projection.fromLatLngToDivPixel(this.bounds_.getSouthWest()); const ne = projection.fromLatLngToDivPixel(this.bounds_.getNorthEast()); sw.x += dx; sw.y += dy; ne.x += dx; ne.y += dy; const newSW = projection.fromDivPixelToLatLng(sw); const newNE = projection.fromDivPixelToLatLng(ne); this.bounds_ = new window.google.maps.LatLngBounds(newSW, newNE); this.draw(); }; const onMouseUp = () => { this.map_.setOptions({ draggable: true }); document.removeEventListener('mousemove', onMouseMove); document.removeEventListener('mouseup', onMouseUp); }; document.addEventListener('mousemove', onMouseMove); document.addEventListener('mouseup', onMouseUp); }); }
        setDraggable(draggable) { this.isDraggable_ = draggable; if (this.div_) { this.div_.style.border = draggable ? '2px dashed #3b82f6' : 'none'; this.div_.style.cursor = draggable ? 'move' : 'default'; } }
        setBounds(bounds) { this.bounds_ = bounds; this.draw(); }
        setRotation(angle) { this.rotation_ = angle; this.draw(); }
        getBounds() { return this.bounds_; }
        getImage() { return this.imageSrc_; }
        getRotation() { return this.rotation_; }
      }
      OverlayClass.current = ManipulableImageOverlay;
    }
  }, [scriptLoaded]);

  useEffect(() => {
    // Se está carregando, não faz nada ainda
    if (isDataGMapsLoading || isImgGMapsLoading) return;

    // Se acabou de configurar localmente, renderiza direto sem modal
    if (localImageConfig) {
      setMapConfig(localImageConfig);
      setShowSetupModal(false);
      return;
    }

    // Se não temos dados de mapa OU imagem, mostra modal de configuração
    if (!dataGMaps || !imgGMaps) {
      setShowSetupModal(true);
      return;
    }

    // Protege contra dados incompletos de bounds
    if (
      dataGMaps.center_lat == null ||
      dataGMaps.center_lng == null ||
      dataGMaps.southwest_lat == null ||
      dataGMaps.southwest_lng == null ||
      dataGMaps.northeast_lat == null ||
      dataGMaps.northeast_lng == null
    ) {
      setShowSetupModal(true);
      return;
    }

    // Se chegou aqui, temos tudo para montar o mapa
    const configFromAPI = {
      center: { lat: parseFloat(dataGMaps.center_lat), lng: parseFloat(dataGMaps.center_lng) },
      bounds: {
        southwest: { lat: parseFloat(dataGMaps.southwest_lat), lng: parseFloat(dataGMaps.southwest_lng) },
        northeast: { lat: parseFloat(dataGMaps.northeast_lat), lng: parseFloat(dataGMaps.northeast_lng) }
      },
      rotation: parseInt(dataGMaps.rotation, 10) || 0,
      zoom: dataGMaps.zoom ? parseInt(dataGMaps.zoom, 10) : 18,
      image: imgGMaps.file,
      initial: false
    };
    setMapConfig(configFromAPI);
    setShowSetupModal(false);
  }, [dataGMaps, imgGMaps, isDataGMapsLoading, isImgGMapsLoading, localImageConfig]);

  useEffect(() => {
    if (allLandsData) {
      const lotesFormatados = allLandsData.map(lote => {
        try {
          const parsedPath = lote.path ? JSON.parse(lote.path) : [];
          return { ...lote, path: Array.isArray(parsedPath) ? parsedPath : [] };
        } catch (e) {
          return { ...lote, path: [] };
        }
      });
      setLotes(lotesFormatados);
    }
  }, [allLandsData]);

  const handleStartSetup = async (lat, lng, imageFile, setLoading) => {
    if (!imageFile) { toast.warn("Por favor, selecione um arquivo de imagem."); return; }
    setLoading(true);
    const notification = toast.loading("Enviando imagem, por favor aguarde...");
    if (!["image/png", "image/jpg", "image/jpeg", "image/webp"].includes(imageFile.type)) {
      toast.update(notification, {
        render: "Tipo de arquivo inválido. Use PNG, JPG ou WEBP.",
        autoClose: 5000,
        type: "error",
        isLoading: false,
      });
      setLoading(false);
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(imageFile);
    reader.onloadend = async () => {
      const base64data = reader.result;
      try {
        const uploadMap = await postMapImage(id, "google", base64data);
        if (uploadMap.success && uploadMap.file) {
          // Calcula bounds mantendo a proporção da imagem
          const img = new window.Image();
          img.onload = async () => {
            const aspect = img.width / img.height;
            const deltaLat = 0.001;
            const deltaLng = deltaLat * aspect;
            const bounds = {
              southwest: { lat: lat - deltaLat, lng: lng - deltaLng },
              northeast: { lat: lat + deltaLat, lng: lng + deltaLng }
            };
            const payload = {
              bounds,
              rotation: 0,
              center: { lat, lng },
              enterprise_id: parseInt(id, 10),
              zoom: 16
            };
            // Usa mutation para atualizar cache e overlay imediatamente
            saveInitialMapMutation.mutate({ payload, imageUrl: uploadMap.file });
            toast.update(notification, {
              render: "Imagem e configuração inicial salvas com sucesso!",
              autoClose: 5000,
              type: "success",
              isLoading: false,
            });
            setLoading(false);
          };
          img.onerror = () => {
            toast.update(notification, {
              render: "Erro ao carregar a imagem para cálculo de proporção.",
              autoClose: 5000,
              type: "error",
              isLoading: false,
            });
            setLoading(false);
          };
          img.src = uploadMap.file;
        } else {
          throw new Error(uploadMap.error || "A resposta do servidor não foi bem-sucedida.");
        }
      } catch (error) {
        console.error("Erro ao enviar o mapa:", error);
        toast.update(notification, {
          render: "Oops, algo deu errado! Tente novamente.",
          autoClose: 5000,
          type: "error",
          isLoading: false,
        });
        setLoading(false);
      }
    };
    reader.onerror = () => {
      toast.update(notification, {
        render: "Erro ao ler o arquivo de imagem.",
        autoClose: 5000,
        type: "error",
        isLoading: false,
      });
      setLoading(false);
    }
  };

  const handleSetInitialView = () => {
    if (!map || !imageOverlay) {
      const toastId = toast.loading("Validando dados...");
      toast.update(toastId, {
        render: "O mapa e o overlay precisam estar carregados.",
        autoClose: 4000,
        type: "warning",
        isLoading: false,
      });
      return;
    }
    // const toastId = toast.loading("Salvando visualização inicial...");
    confirmDialog({
      message: "Deseja salvar a visualização atual (posição e zoom) como a visualização inicial do mapa?",
      header: "Salvar Visualização Inicial",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: () => {
        const payload = {
          bounds: {
            southwest: imageOverlay.getBounds().getSouthWest().toJSON(),
            northeast: imageOverlay.getBounds().getNorthEast().toJSON()
          },
          rotation: imageOverlay.getRotation(),
          center: map.getCenter().toJSON(),
          zoom: map.getZoom(),
          enterprise_id: parseInt(id, 10)
        };
        saveMapMutation.mutate(payload);
        // saveMapMutation.mutate(payload, { onSettled: () => toast.dismiss(toastId) });
      },
      reject: () => toast.warn("Alterações descartadas."),
    });
  };

  const toggleOverlayEditing = (forceState) => {
    if (!imageOverlay) return;
    const nextState = typeof forceState === 'boolean' ? forceState : !isEditingOverlay;
    setIsEditingOverlay(nextState);
    imageOverlay.setDraggable(nextState);
    if (!nextState) {
      // const toastId = toast.loading("Salvando alterações do overlay...");
      confirmDialog({
        message: "Deseja salvar as alterações do overlay?",
        header: "Salvar Alterações",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Sim",
        rejectLabel: "Não",
        accept: () => {
          const currentBounds = imageOverlay.getBounds();
          const payload = {
            bounds: {
              southwest: currentBounds.getSouthWest().toJSON(),
              northeast: currentBounds.getNorthEast().toJSON()
            },
            rotation: imageOverlay.getRotation(),
            center: map.getCenter().toJSON(),
            zoom: map.getZoom(),
            enterprise_id: parseInt(id, 10)
          };
          // saveMapMutation.mutate(payload, { onSettled: () => toast.dismiss(toastId) });
          saveMapMutation.mutate(payload);
        },
        reject: () => toast.warn("Alterações descartadas."),
      });
    }
  };

  const handleSaveLote = (formData) => {
    if (!formData.block_id) {
      // const toastId = toast.loading("Validando dados...");
      confirmDialog({
        message: `É necessário selecionar uma ${nomenclature[0].value} e um ${nomenclature[1].value} na barra lateral antes de salvar o desenho.`,
        header: "Atenção",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Ok",
        rejectLabel: "",
        accept: () => toast.info("Desenho do lote não foi salvo, pois a quadra não foi selecionada."),
        reject: () => toast.info("Desenho do lote não foi salvo, pois a quadra não foi selecionada."),
      });
      return;
    }
    const loteData = {
      ...formData,
      id: (formData.id),
      name: (formData.name || "").trim(),
      m2: (formData.m2 || ""),
      value: (formData.value || ""),
      status: formData.status || "1",
      obs: (formData.obs || "").trim(),
      path: formData.path && formData.path.length > 0
        ? formData.path
        : (currentPolygonRef.current
          ? currentPolygonRef.current.getPath().getArray().map(v => ({ lat: v.lat(), lng: v.lng() }))
          : []),
    };
    if (!loteData.id || !loteData.name) {
      // const toastId = toast.loading("Validando dados...");
      confirmDialog({
        message: "Os campos 'ID do Lote' e 'Nome' são obrigatórios.",
        header: "Atenção",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Ok",
        rejectLabel: "",
        accept: () => toast.info("Desenho do lote não foi salvo, pois a quadra não foi selecionada."),
        reject: () => toast.info("Desenho do lote não foi salvo, pois a quadra não foi selecionada."),
      });
      return;
    }
    if (!loteData.path || loteData.path.length < 3) {
      // const toastId = toast.loading("Validando dados...");
      confirmDialog({
        message: "O polígono do lote precisa ter pelo menos 3 vértices.",
        header: "Atenção",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Ok",
        rejectLabel: "",
        accept: () => toast.info("Desenho do lote não foi salvo, pois o polígono é inválido."),
        reject: () => toast.info("Desenho do lote não foi salvo, pois o polígono é inválido."),
      });
      return;
    }
    const apiPayload = {
      ...loteData,
      block_id: formData.block_id,
      path: JSON.stringify(loteData.path),
      enterprise_id: parseInt(id, 10),
    };
    const payloadCompleto = {
      ...apiPayload,
      landId: loteData.id,
    };
    updateLandMutation.mutate(payloadCompleto);
    setSelectedLote(null);
    if (currentPolygonRef.current) {
      currentPolygonRef.current.setMap(null);
      currentPolygonRef.current = null;
    }
  };

  const handleDeleteLote = (loteParaExcluir) => {
    if (!loteParaExcluir.id_block) {
      // const toastId = toast.loading("Validando dados...");
      confirmDialog({
        message: "Não foi possível identificar a quadra deste lote para remover o desenho. Ação cancelada.",
        header: "Atenção",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Ok",
        rejectLabel: "",
        accept: () => toast.info("Desenho do lote não foi removido, pois a quadra não foi selecionada."),
        reject: () => toast.info("Desenho do lote não foi removido, pois a quadra não foi selecionada."),
      });
      return;
    }
    // const toastId = toast.loading("Removendo desenho do lote...");
    confirmDialog({
      message: `Tem certeza que deseja remover o desenho do mapa para o lote "${loteParaExcluir.name}"?`,
      header: "Remover Desenho do Lote",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: () => {
        const payloadCompleto = {
          ...loteParaExcluir,
          landId: loteParaExcluir.id,
          block_id: loteParaExcluir.id_block,
          path: null,
          enterprise_id: parseInt(id, 10),
        };
        updateLandMutation.mutate(payloadCompleto);
        // toast.dismiss(toastId);
      },
      reject: () => toast.info("Desenho do lote não foi removido, pois a quadra não foi selecionada."),
    });
  };

  const startDrawing = () => {
    if (drawingManager) {
      setSelectedLote(null);
      if (currentPolygonRef.current) {
        currentPolygonRef.current.setMap(null);
        currentPolygonRef.current = null;
      }
      drawingManager.setDrawingMode(window.google.maps.drawing.OverlayType.POLYGON);
    }
  };

  // Função para dar zoom e centralizar no lote selecionado
  const zoomToLote = (lote) => {
    if (!map || !lote || !lote.path || lote.path.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    lote.path.forEach(coord => bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng)));
    map.fitBounds(bounds);
    // Opcional: se quiser um zoom máximo, pode limitar aqui
    // const listener = window.google.maps.event.addListenerOnce(map, "bounds_changed", () => {
    //   if (map.getZoom() > 20) map.setZoom(20);
    // });
  };

  useEffect(() => {
    if (!scriptLoaded || !mapRef.current || map) return;
    if (!mapConfig) return;
    const center = mapConfig.center || { lat: -23.55052, lng: -46.633308 };
    const zoom = mapConfig.zoom || 18;
    const mapInstance = new window.google.maps.Map(mapRef.current, {
      center,
      zoom,
      mapTypeId: 'satellite',
      disableDefaultUI: true,
      gestureHandling: 'greedy',
      tilt: 0
    });
    setMap(mapInstance);
  }, [scriptLoaded, mapConfig, map]);

  useEffect(() => {
    if (!map || !OverlayClass.current) return;
    const dm = new window.google.maps.drawing.DrawingManager({
      drawingMode: null,
      drawingControl: false,
      polygonOptions: {
        fillColor: '#22c55e', fillOpacity: 0.5,
        strokeWeight: 2, strokeColor: '#ffffff',
        editable: false, zIndex: 10,
      },
    });
    dm.setMap(map);
    setDrawingManager(dm);
    window.google.maps.event.addListener(dm, "polygoncomplete", (polygon) => {
      currentPolygonRef.current = polygon;
      setSelectedLote({
        id: "",
        name: "",
        m2: "",
        value: "",
        status: "disponível",
        obs: "",
        path: polygon.getPath().getArray().map(v => ({ lat: v.lat(), lng: v.lng() })),
      });
      dm.setDrawingMode(null);
    });
    if (mapConfig && mapConfig.image && imgGMaps?.file) {
      const bounds = new window.google.maps.LatLngBounds(mapConfig.bounds.southwest, mapConfig.bounds.northeast);
      const overlay = new OverlayClass.current(bounds, imgGMaps.file, map);
      overlay.setRotation(mapConfig.rotation || 0);
      setImageOverlay(overlay);
    }
  }, [map, imgGMaps]);

  useEffect(() => {
    if (!map) return;
    polygons.forEach(p => p.setMap(null));
    const newPolygons = new Map();
    lotes.forEach(lote => {
      const polygon = new window.google.maps.Polygon({
        paths: lote.path,
        strokeColor: "#FFFFFF",
        strokeOpacity: 0.9,
        strokeWeight: (selectedLote && selectedLote.id === lote.id) ? 3 : 1.5,
        fillColor: getStatusColor(lote.status),
        fillOpacity: 0.6,
        zIndex: (selectedLote && selectedLote.id === lote.id) ? 5 : 2,
        map: map
      });
      polygon.addListener('click', () => {
        setSelectedLote(lote);
        setIsSidebarOpen(true);
        zoomToLote(lote); // Zoom ao clicar no mapa
      });
      newPolygons.set(lote.id, polygon);
    });
    setPolygons(newPolygons);
    return () => {
      newPolygons.forEach(p => p.setMap(null));
    };
  }, [map, lotes, selectedLote, type]);

  const getStatusColor = (status) => ({ '1': '#34A853', '0': '#FBBC05', '2': '#EA4335', '3': '#2d2d2dcc' }[status] || '#4285F4');
  const getStatusName = (status) => ({ '1': 'Disponível', '0': 'Reservado', '2': 'Vendido', '3': 'Bloqueado' }[status] || 'Indefinido');

  return (
    <div className="w-100 vh-100 d-flex flex-column font-sans" style={{ height: '80%' }}>
      {type === "settings" && (
        <>
          {showSetupModal && <SetupModal onStart={handleStartSetup} />}
          <Header
            onEditOverlay={toggleOverlayEditing}
            isEditingOverlay={isEditingOverlay}
            onNewLote={startDrawing}
            onSetInitialView={handleSetInitialView}
          />
        </>
      )}
      <div className="d-flex flex-grow-1 overflow-hidden position-relative">
        <div ref={mapRef} className="flex-grow-1" />
        {type === "settings" && (
          <>
            {isEditingOverlay && <ImageControls overlay={imageOverlay} />}
          </>
        )}
        {isSidebarOpen && (
          <Sidebar
            lotes={lotes}
            selectedLote={selectedLote}
            onSelectLote={(lote) => {
              setSelectedLote(lote);
              zoomToLote(lote); // Zoom ao clicar na sidebar
            }}
            onSave={handleSaveLote}
            onDelete={handleDeleteLote}
            getStatusName={getStatusName}
            empId={id}
            nomenclatures={nomenclatures}
            isAllLandsLoading={isAllLandsLoading}
            type={type}
            navigate={navigate}
          />
        )}
      </div>
    </div>
  );
};

const Header = ({ onEditOverlay, isEditingOverlay, onNewLote, onSetInitialView }) => (
  <header className="card px-2 mb-4  z-20 flex-shrink-0">
    <div className="container px-4 py-3 d-flex justify-content-between align-items-center">
      <div className="d-flex align-items-center gap-3">
        <h4 className="fw-semibold m-0">Configuração Google Maps</h4>
      </div>
      <div className="controls d-flex gap-2">
        {isEditingOverlay && (
          <Button
            label="Definir Posição Inicial"
            icon="pi pi-map-marker"
            className="p-button-info fw-semibold"
            onClick={onSetInitialView}
          />
        )}
        <Button
          label={isEditingOverlay ? "Salvar Ajustes" : "Ajustar Mapa"}
          icon={isEditingOverlay ? "pi pi-save" : "pi pi-cog"}
          className="p-button-text p-button-primary fw-semibold"
          onClick={onEditOverlay}
        />
        <Button
          label="Novo Lote"
          icon="pi pi-plus"
          severity='primary'
          className="p-button-primary flex-grow-1"
          type="button"
          onClick={onNewLote}
        />
      </div>
    </div>
  </header>
);

const ImageControls = ({ overlay }) => {
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);
  const [lat, setLat] = useState(0);
  const [lng, setLng] = useState(0);
  const originalDeltaRef = useRef({ lat: 0, lng: 0 });

  useEffect(() => {
    if (overlay) {
      setRotation(overlay.getRotation());
      const bounds = overlay.getBounds();
      const center = bounds.getCenter();
      setLat(center.lat());
      setLng(center.lng());
      const sw = bounds.getSouthWest();
      originalDeltaRef.current = {
        lat: Math.abs(center.lat() - sw.lat()),
        lng: Math.abs(center.lng() - sw.lng()),
      };
      const currentDeltaLat = Math.abs(center.lat() - sw.lat());
      const initialDeltaLat = originalDeltaRef.current.lat;
      if (initialDeltaLat > 0) {
        setScale(currentDeltaLat / initialDeltaLat);
      } else {
        setScale(1);
      }
    }
  }, [overlay]);

  const handleRotationChange = (e) => {
    const newRotation = parseInt(e.target.value, 10);
    setRotation(newRotation);
    if (overlay) overlay.setRotation(newRotation);
  };

  const updateScale = (newScalePercent) => {
    const newScaleValue = parseFloat(newScalePercent) / 100;
    setScale(newScaleValue);
    if (overlay) {
      const center = overlay.getBounds().getCenter();
      const { lat: deltaLat, lng: deltaLng } = originalDeltaRef.current;
      const newDeltaLat = deltaLat * newScaleValue;
      const newDeltaLng = deltaLng * newScaleValue;
      const newSW = { lat: center.lat() - newDeltaLat, lng: center.lng() - newDeltaLng };
      const newNE = { lat: center.lat() + newDeltaLat, lng: center.lng() + newDeltaLng };
      const newBounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(newSW.lat, newSW.lng),
        new window.google.maps.LatLng(newNE.lat, newNE.lng)
      );
      overlay.setBounds(newBounds);
    }
  };

  const handleNudge = (direction) => {
    const nudgeAmount = 0.00004;
    let newLat = lat;
    let newLng = lng;
    switch (direction) {
      case 'up': newLat += nudgeAmount; break;
      case 'down': newLat -= nudgeAmount; break;
      case 'left': newLng -= nudgeAmount; break;
      case 'right': newLng += nudgeAmount; break;
      default: return;
    }
    setLat(newLat);
    setLng(newLng);
    if (overlay) {
      const { lat: deltaLat, lng: deltaLng } = originalDeltaRef.current;
      const currentScale = scale;
      const newDeltaLat = deltaLat * currentScale;
      const newDeltaLng = deltaLng * currentScale;
      const newSW = { lat: newLat - newDeltaLat, lng: newLng - newDeltaLng };
      const newNE = { lat: newLat + newDeltaLat, lng: newLng + newDeltaLng };
      const newBounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(newSW.lat, newSW.lng),
        new window.google.maps.LatLng(newNE.lat, newNE.lng)
      );
      overlay.setBounds(newBounds);
    }
  };

  return (
    <div
      className="position-absolute top-0 end-0 mt-4 me-4 bg-white p-3 rounded-3 shadow-lg d-flex flex-column gap-3"
      style={{ width: 280, zIndex: 10 }}
    >
      <div>
        <label className="form-label mb-1">
          Rotação: <span>{rotation}</span>°
        </label>
        <input
          type="range" min="0" max="360" value={rotation} step="1"
          className="form-range"
          onChange={handleRotationChange}
        />
      </div>
      <div>
        <label className="form-label mb-1">
          Escala: <span>{Math.round(scale * 100)}</span>%
        </label>
        <div className="d-flex align-items-center gap-2">
          <Button icon="pi pi-minus" className="p-button-secondary p-button-sm" onClick={() => updateScale(Math.round(scale * 100) - 1)} />
          <input
            type="range" min="10" max="500"
            value={Math.round(scale * 100)}
            step="1"
            className="form-range"
            onChange={(e) => updateScale(e.target.value)}
          />
          <Button icon="pi pi-plus" className="p-button-secondary p-button-sm" onClick={() => updateScale(Math.round(scale * 100) + 1)} />
        </div>
      </div>
      <div>
        <label className="form-label mb-2 text-center d-block">Posição</label>
        <div className="d-flex justify-content-center">
          <div style={{ width: 120 }}>
            <div className="d-flex justify-content-center">
              <Button icon="pi pi-arrow-up" className="p-button-primary p-button-sm" onClick={() => handleNudge('up')} />
            </div>
            <div className="d-flex justify-content-between my-1">
              <Button icon="pi pi-arrow-left" className="p-button-primary p-button-sm" onClick={() => handleNudge('left')} />
              <Button icon="pi pi-arrow-right" className="p-button-primary p-button-sm" onClick={() => handleNudge('right')} />
            </div>
            <div className="d-flex justify-content-center">
              <Button icon="pi pi-arrow-down" className="p-button-primary p-button-sm" onClick={() => handleNudge('down')} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SetupModal = ({ onStart }) => {
  const [lat, setLat] = useState(-22.7530);
  const [lng, setLng] = useState(-47.4012);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const miniMapRef = useRef(null);
  const [miniMap, setMiniMap] = useState(null);
  const [marker, setMarker] = useState(null);

  useEffect(() => {
    if (window.google && miniMapRef.current && !miniMap) {
      const mapInstance = new window.google.maps.Map(miniMapRef.current, {
        center: { lat, lng },
        zoom: 13,
        mapTypeControl: false,
        streetViewControl: false,
      });
      setMiniMap(mapInstance);
    }
  }, [miniMap, lat, lng]);

  useEffect(() => {
    if (miniMap) {
      const clickListener = miniMap.addListener('click', (e) => {
        const clickedLat = e.latLng.lat();
        const clickedLng = e.latLng.lng();
        setLat(clickedLat);
        setLng(clickedLng);
        if (marker) {
          marker.setPosition(e.latLng);
        } else {
          const newMarker = new window.google.maps.Marker({
            position: e.latLng,
            map: miniMap,
          });
          setMarker(newMarker);
        }
        miniMap.panTo(e.latLng);
      });
      if (!marker) {
        const initialMarker = new window.google.maps.Marker({
          position: { lat, lng },
          map: miniMap,
        });
        setMarker(initialMarker);
      }
      return () => {
        window.google.maps.event.removeListener(clickListener);
      };
    }
  }, [miniMap, marker]);

  return (
    <div className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-flex align-items-center justify-content-center z-3">
      <div className="bg-white p-4 rounded-3 shadow-lg w-100" style={{ maxWidth: 500 }}>
        <h2 className="h4 fw-bold text-dark mb-3">Configuração Inicial</h2>
        <div className="mb-3">
          <p className="form-label mb-2">1. Clique no mapa para definir a localização central do seu loteamento:</p>
          <div ref={miniMapRef} style={{ height: '300px', width: '100%', borderRadius: '0.25rem' }} />
          <div className="mt-2 small text-secondary">
            <span>Latitude: {lat.toFixed(6)}</span> | <span>Longitude: {lng.toFixed(6)}</span>
          </div>
        </div>
        <div className="mb-3">
          <label className="form-label">2. Selecione a imagem da planta:</label>
          <input
            type="file"
            accept="image/*"
            onChange={e => setFile(e.target.files[0])}
            className="form-control"
            disabled={loading}
          />
        </div>
        <Button
          label={loading ? "Enviando..." : "Iniciar Edição"}
          icon={loading ? "pi pi-spin pi-spinner" : "pi pi-check"}
          className="p-button-primary w-100 fw-semibold mt-3"
          onClick={() => onStart(lat, lng, file, setLoading)}
          disabled={!file || loading}
        />
      </div>
    </div>
  );
};

const Sidebar = ({ lotes, selectedLote, onSelectLote, onSave, onDelete, getStatusName, empId, nomenclatures, isAllLandsLoading, type, navigate }) => {
  const nomenclature = JSON.parse(nomenclatures);
  const [formData, setFormData] = useState(null);
  const [currentBlock, setCurrentBlock] = useState(false);
  const [currentLand, setCurrentLand] = useState(false);

  const {
    data: blocks,
    isError,
    isLoading,
  } = useQuery({
    queryKey: [`blocks${empId}`],
    queryFn: () => getBlocks(empId),
    retry: false,
    cacheTime: 0,
  });

  const {
    data: lands,
    isError: isLandsError,
    isLoading: isLandsLoading,
  } = useQuery({
    queryKey: [`lands_${currentBlock}`],
    queryFn: () => getLands(currentBlock),
    retry: false,
    cacheTime: 0,
    enabled: !!currentBlock,
  });

  useEffect(() => {
    if (currentLand && lands) {
      const selectedLand = lands.find(l => l.id === currentLand);
      if (selectedLand) {
        setFormData(prev => {
          if (prev && prev.id === selectedLand.id) {
            return prev;
          }
          const existingPath = prev?.path;
          return {
            ...selectedLand,
            path: existingPath,
          };
        });
      }
    }
  }, [currentLand, lands]);

  useEffect(() => {
    setFormData(selectedLote ? { ...selectedLote } : null);
  }, [selectedLote]);

  const handleSave = () => {
    if (formData) {
      const dataParaSalvar = {
        ...formData,
        block_id: currentBlock
      };
      onSave(dataParaSalvar);
    }
  };

  const handleDelete = () => {
    if (formData) {
      onDelete(formData);
    }
  };

  const handleSelect = (lote) => {
    onSelectLote(lote);
  };

  return (
    <div
      className="card  border-start p-3 d-flex flex-column"
      style={{ width: 380, minWidth: 320 }}
    >
      {/* Editor de lotes para settings/config */}
      {(formData && (type === "settings" || type === "config")) && (
        <>
          <h2 className="h5 fw-bold mb-3">Editor de Lotes</h2>
          {!isLoading && (
            <>
              <div className="row mb-3">
                <div className="col-md-6 col-sm-12">
                  <label className="mb-3">{nomenclature[0].value}</label>
                  <Dropdown
                    filter
                    showClear
                    value={currentBlock}
                    onChange={(e) => {
                      setCurrentBlock(e.value);
                      setCurrentLand(null);
                    }}
                    options={blocks ? Object.keys(blocks).map((index) => {
                      const dado = blocks[index];
                      return {
                        value: dado.id,
                        label: dado.name,
                      };
                    }) : []}
                    disabled={isLoading}
                    placeholder={isLoading ? "Carregando..." : "Selecione"}
                    optionLabel="label"
                    className="bg-none px-0 w-100"
                  />
                </div>

                <div className="col-md-6 col-sm-12">
                  <label className="mb-3">{nomenclature[1].value}</label>
                  <Dropdown
                    filter
                    showClear
                    value={currentLand}
                    onChange={(e) => setCurrentLand(e.value)}
                    options={lands?.map((land) => ({
                      value: land.id,
                      label: land.name,
                    }))}
                    optionLabel="label"
                    disabled={!currentBlock || isLandsLoading}
                    emptyMessage="Não há lotes"
                    placeholder={
                      currentBlock && isLandsLoading ? "Carregando..." : "Selecione"
                    }
                    className="bg-none px-0 w-100"
                  />
                </div>
              </div>
              <div className=" p-3 rounded-3 mb-3">
                <div className="d-flex gap-2">
                  <Button
                    label="Salvar"
                    icon="pi pi-save"
                    className="p-button-primary flex-grow-1"
                    type="button"
                    onClick={handleSave}
                  />
                  <Button
                    label="Excluir"
                    icon="pi pi-trash"
                    className="p-button-danger flex-grow-1"
                    type="button"
                    onClick={handleDelete}
                  />
                </div>
              </div>
            </>
          )}
        </>
      )}
      {/* Dados do lote e botão de navegação para view */}
      {(formData && type === "view") && (
        <div className="mb-3">
          <h2 className="h5 fw-bold mb-2">Dados do Lote</h2>
          <div className="mb-2"><strong>{nomenclature[0].value}:</strong> {formData.block} - {nomenclature[1].value}: {formData.name}</div>
          <div className="mb-2"><strong>Status:</strong> {getStatusName(formData.status)}</div>
          <div className="mb-2"><strong>Área:</strong> {formData.m2} m²</div>
          <div className="mb-2"><strong>Valor:</strong> {formData.value ? formatCurrency(formData.value) : '-'}</div>
          <div className="mb-2"><strong>Observação:</strong> {formData.obs}</div>
          {formData.status === 1 && (
            <Button
              label="Ir para Simulador"
              icon="pi pi-directions"
              className="p-button-primary w-100 fw-semibold mt-2"
              onClick={() => navigate(`/enterprise/simulator/${formData.id}`, { state: { empId } })}
            />
          )}
        </div>
      )}

      <h3 className="fw-bold mb-2 fs-6">Lotes Cadastrados</h3>
      <div className="overflow-auto" style={{ maxHeight: "60vh" }}>
        {(lotes.length === 0 && !isAllLandsLoading) && (
          <p className="text-secondary">Nenhum lote para exibir.</p>
        )}
        {isAllLandsLoading && <p className="text-secondary">Carregando lotes...</p>}
        {lotes
          .filter(lote => lote.path && lote.path.length > 0)
          .map((lote) => (
            <div
              key={lote.id}
              onClick={() => handleSelect(lote)}
              className={
                "p-2 mb-2 rounded-3 border " +
                (selectedLote && selectedLote.id === lote.id
                  ? "bg-primary bg-opacity-10 border-primary fw-bold"
                  : "bg-white border-light")
              }
              style={{ cursor: "pointer" }}
            >
              <div className='d-flex justify-content-between'>
                <p className="mb-0">
                  {nomenclature[0].value}: {lote.block} - {nomenclature[1].value}: {lote.name}
                </p>
              </div>
              <div className="small text-secondary">
                {lote.m2 ? `${lote.m2} m²` : ""}{" "}
                {lote.value ? ` | ${formatCurrency(lote.value)}` : ""}
              </div>
              <span className="badge rounded-pill bg-light text-dark mt-1">
                {getStatusName(lote.status)}
              </span>
            </div>
          ))}
      </div>
    </div>
  );
};